import EditIcon from "@mui/icons-material/Edit";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Fab from "@mui/material/Fab";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Popover from "@mui/material/Popover";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import { ConfirmationDialog } from "../../components/ConfirmationDialog/ConfirmationDialog";
import { KPISystemCopyToTenantDialog } from "../../components/KPISystem/KPISystemCopyToTenantDialog/KPISystemCopyToTenantDialog";
import { StatusFilter } from "../../components/KPISystem/StatusFilter/StatusFilter";
import { LoadingOverlay } from "../../components/LoadingOverlay/LoadingOverlay";
import { SimpleTableWithSort } from "../../components/SimpleTableWithSort/SimpleTableWithSort";
import { StatusBadge } from "../../components/StatusBadge/StatusBadge";
import { MIN_PAGE_SIZE, PAGE_SIZES } from "../../constants/main";
import {
  PERMISSION_KPI_SYSTEM_CREATE,
  PERMISSION_KPI_SYSTEM_EDIT,
  PERMISSION_KPI_SYSTEM_MIGRATION,
} from "../../constants/permissions";
import { hasPermission } from "../../helpers/permission";
import { checkPopState } from "../../hooks";
import { showMuiDialog } from "../../hooks/dialog";
import {
  cloneKpiSystem,
  deleteKpiSystem,
  getKpiSystems,
  resetKpiSystemsPageState,
  useKpiSystemCloneResponse,
  useKpiSystemsDeleteResponse,
  useKpiSystemsPageLogic,
  useKpiSystemsResponse,
} from "../../hooks/kpiSystem";
import { showError, showInfo, showSuccess } from "../../hooks/toast";
import { useTimeout } from "../../hooks/utils/useTimeout";
import { PlusSVG } from "../../svg";
import { processMessage } from "../../utils";

const AuthorColumnTemplate = (props, field) => (
  <NavLink to={"/sustainabilitySystem/kpiSystem/" + props.id} className="column-link">
    {!!props.readOnly && <LockOutlinedIcon />}
    <div className={props.readOnly ? "column-link-with-icon" : ""}>{props[field]}</div>
  </NavLink>
);

const NameColumnTemplate = (props, field) => (
  <NavLink to={"/sustainabilitySystem/kpiSystem/" + props.id} className="column-link">
    {props[field]}
  </NavLink>
);

const StatusColumnTemplate = (props, t) =>
  !!props.status && (
    <StatusBadge className={"status-" + props.status}>{t("kpiSystem.status." + props.status)}</StatusBadge>
  );

const ActionsColumnTemplate = (props, setDeleteConfirmation, rootProps, setKebabPopoverTarget) => (
  <>
    {hasPermission(PERMISSION_KPI_SYSTEM_EDIT) && (
      <NavLink to={"/sustainabilitySystem/kpiSystem/" + props.id + "?mode=edit"}>
        <IconButton color="primary" size="small" disabled={props.readOnly}>
          <EditIcon />
        </IconButton>
      </NavLink>
    )}
    {!hasPermission(PERMISSION_KPI_SYSTEM_EDIT) && (
      <NavLink to={"/sustainabilitySystem/kpiSystem/" + props.id}>
        <IconButton color="primary" size="small">
          <VisibilityIcon />
        </IconButton>
      </NavLink>
    )}
    {hasPermission(PERMISSION_KPI_SYSTEM_EDIT) && (
      <IconButton size="small" onClick={(event) => setKebabPopoverTarget({ target: event.target, props, rootProps })}>
        <MoreVertIcon />
      </IconButton>
    )}
  </>
);

export const KPISystemsPage = () => {
  const { t } = useTranslation();
  const response = useKpiSystemsResponse();
  const systems = response.data || [];

  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const deleteResponse = useKpiSystemsDeleteResponse();
  const cloneResponse = useKpiSystemCloneResponse();

  const {
    searchValue,
    setSearchValue,
    filterValue,
    setFilterValue,
    sortValue,
    setSortValue,
    page,
    rowsPerPage,
    setPage,
    setRowsPerPage,
    resetState,
  } = useKpiSystemsPageLogic();
  const [, setSearchTimeout] = useTimeout();

  const [processed, setProcessed] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [kebabPopoverTarget, setKebabPopoverTarget] = useState(null);

  const load = () => getKpiSystems().catch(console.error);

  useEffect(() => {
    if (!checkPopState()) {
      resetKpiSystemsPageState();
    }
    load();
  }, []);

  useEffect(() => {
    if (!response.loading) {
      let updated = [...systems];
      // search
      if (searchValue) {
        updated = updated.filter(
          (item) =>
            (item.name || "").toLowerCase().indexOf(searchValue.toLowerCase()) !== -1 ||
            (item.systemSource || "").toLowerCase().indexOf(searchValue.toLowerCase()) !== -1 ||
            (item.systemVersion || "").toLowerCase().indexOf(searchValue.toLowerCase()) !== -1 ||
            (item.description || "").toLowerCase().indexOf(searchValue.toLowerCase()) !== -1
        );
      }
      setFiltered(updated);
      if (filterValue) {
        updated = updated.filter((item) => item.status === filterValue);
      }
      // sort
      if (sortValue.field) {
        updated = updated.sort((a, b) => {
          if (a[sortValue.field] > b[sortValue.field]) {
            return sortValue.desc ? -1 : 1;
          } else if (a[sortValue.field] < b[sortValue.field]) {
            return sortValue.desc ? 1 : -1;
          } else {
            return 0;
          }
        });
      }
      setProcessed(updated);
      if (page * rowsPerPage > updated.length) {
        setPage(Math.floor(updated.length / rowsPerPage));
      }
    }
  }, [response.loading, searchValue, sortValue, page, rowsPerPage, filterValue]);

  const onSort = (field) =>
    setSortValue({
      field: field !== sortValue.field || !sortValue.desc ? field : null,
      desc: sortValue.field === field ? !sortValue.desc : false,
    });

  const search = (value) => {
    setSearchTimeout(setTimeout(() => setSearchValue(String(value || "").trim()), 500));
  };

  const afterChange = () => setPage(0) & load();

  const openMigrateSystemDialog = (kpiSystemId) =>
    showMuiDialog((props) => <KPISystemCopyToTenantDialog kpiSystemId={kpiSystemId} {...props} />);

  const deleteHandler = () => {
    const kpiSystemId = deleteConfirmation;
    setDeleteConfirmation(false);
    deleteKpiSystem(kpiSystemId).then(
      () => {
        showInfo(t("kpiSystem.deletedMessage"));
        afterChange();
      },
      () => showError(t("kpiSystem.deleteError"))
    );
  };

  const cloneHandler = (id) => {
    cloneKpiSystem(id).then(
      () => {
        showSuccess(t("sustainabilitySystem.cloneCreatedSuccessMessage"));
        afterChange();
      },
      () => showError(t("sustainabilitySystem.cloneCreatedErrorMessage"))
    );
  };

  const pagination = {
    labelRowsPerPage: t("pagination.rowsPerPage"),
    labelDisplayedRows: ({ from, to, count }) => processMessage(t("pagination.displayedRows"), [from, to, count]),
    rowsPerPageOptions: PAGE_SIZES,
    count: processed.length,
    page: page,
    show: processed.length > MIN_PAGE_SIZE ? "true" : "false",
    onPageChange: (event, pagePagination) => setPage(pagePagination),
    rowsPerPage: rowsPerPage,
    onRowsPerPageChange: (event) => setRowsPerPage(event.target.value),
  };

  const paged = [...processed].splice(page * rowsPerPage, rowsPerPage);

  return (
    <div className="kpi-sustainability-systems page-with-table">
      <div className="page-header large flex-row">
        <div className="flex-col title-box no-margin">
          <h1>{t("kpiSystems.title")}</h1>
        </div>
        <div className="flex-col search-box no-margin">
          <SearchIcon />
          <input
            type="text"
            key={resetState}
            defaultValue={searchValue || ""}
            className="search-input input-shadow"
            placeholder={t("main.search")}
            onChange={(event) => search(event.target.value)}
          />
        </div>
      </div>

      <div className="page-layout">
        <div className="flex-row-filter">
          <StatusFilter onChange={setFilterValue} value={filterValue} records={filtered} />
        </div>

        <LoadingOverlay
          spinner
          className="table-loading"
          active={response.loading || deleteResponse.loading || cloneResponse.loading}
        >
          <SimpleTableWithSort
            className="repaint"
            data={paged}
            pagination={pagination}
            setFilterValue={setFilterValue}
            filterValue={filterValue}
            sortValue={sortValue}
            onSort={onSort}
            columns={[
              {
                field: "systemSource",
                sortable: true,
                headerText: t("kpiSystem.systemSource"),
                template: AuthorColumnTemplate,
              },
              {
                field: "name",
                sortable: true,
                headerText: t("kpiSystems.name"),
                template: NameColumnTemplate,
              },
              {
                field: "systemVersion",
                sortable: true,
                headerText: t("kpiSystem.systemVersionShort"),
              },
              {
                field: "description",
                sortable: true,
                headerText: t("kpiSystems.description"),
              },
              {
                field: "status",
                sortable: true,
                className: "status-cell",
                headerText: t("kpiSystems.status"),
                template: (props) => StatusColumnTemplate(props, t),
              },
              {
                className: "action-cell",
                headerClassName: "action-header-cell",
                headerText: t("main.actions"),
                template: (props, rootProps) =>
                  ActionsColumnTemplate(props, setDeleteConfirmation, rootProps, setKebabPopoverTarget),
              },
            ]}
          />
        </LoadingOverlay>
      </div>

      {hasPermission(PERMISSION_KPI_SYSTEM_CREATE) && (
        <div className="fabs">
          <Fab type="button" color="primary">
            <NavLink to="/sustainabilitySystem/kpiSystem/create" className="link">
              <PlusSVG />
            </NavLink>
          </Fab>
        </div>
      )}

      <Popover
        open={!!kebabPopoverTarget}
        anchorEl={(kebabPopoverTarget || {}).target}
        onClose={() => setKebabPopoverTarget(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {hasPermission(PERMISSION_KPI_SYSTEM_CREATE) && (
          <MenuItem
            onClick={() => {
              setKebabPopoverTarget(null);
              cloneHandler((kebabPopoverTarget || {}).props?.id);
            }}
          >
            {t("main.clone")}
          </MenuItem>
        )}
        {hasPermission(PERMISSION_KPI_SYSTEM_MIGRATION) && (
          <MenuItem
            onClick={() => {
              setKebabPopoverTarget(null);
              openMigrateSystemDialog((kebabPopoverTarget || {}).props?.id);
            }}
          >
            {t("kpiSystem.migrate")}
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setKebabPopoverTarget(null);
            setDeleteConfirmation((kebabPopoverTarget || {}).props?.id);
          }}
        >
          {t("main.delete")}
        </MenuItem>
      </Popover>

      <ConfirmationDialog
        open={!!deleteConfirmation}
        onClose={() => setDeleteConfirmation(null)}
        onConfirm={deleteHandler}
        showLineBreaks
        titleText={t("kpiSystem.deleteConfirmationTitle")}
        bodyText={t("kpiSystem.deleteConfirmation")}
        confirmText={t("main.delete")}
        color="secondary"
      />
    </div>
  );
};
